import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Invoice, CreateInvoice } from '@thro-types/jobmarket';

import { AxiosRequestConfig } from 'axios';

import {
  createJobmarketInvoice,
  getBillableCompanies,
  getBillableJobPeriodsCount,
  getJobmarketInvoice,
  getJobmarketInvoices,
  getPeriodsForOffers
} from 'api/jobmarketService';

/**
 * A custom hook for all invoices from the Jobmarket Service.
 *
 * @returns UseQueryResult<Invoice[], Error>
 */
export const useJobmarketInvoicesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobmarket-invoices', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const invoices = getJobmarketInvoices(config);
      return invoices;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useJobmarketInvoiceQuery = (id?: number, config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobmarket-invoices', id, config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      if (!id) {
        return;
      }
      const invoice = getJobmarketInvoice(id, config);
      return invoice;
    },
    enabled: !!id
  });

  return query;
};

export const useJobmarketBillableJobPeriodsCountQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobmarket-billable-periods-count', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const invoice = getBillableJobPeriodsCount(config);
      return invoice;
    }
  });

  return query;
};

export const useJobmarketBillableCompaniesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobmarket-billable-companies', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const invoice = getBillableCompanies(config);
      return invoice;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useJobmarketPeriodsForOffersQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['jobmarket-periods-for-offers', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const invoice = getPeriodsForOffers(config);
      return invoice;
    },
    placeholderData: keepPreviousData
  });

  return query;
};

export const useJobmarketInvoicesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['jobmarket-invoices'];
  const create = useMutation<Invoice, Error, CreateInvoice>({
    mutationFn: async args => {
      return await createJobmarketInvoice(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['jobmarket-billable-periods-count'] });
      queryClient.invalidateQueries({ queryKey: ['jobmarket-billable-companies'] });
    },
    onError: data => {}
  });

  return { create } as const;
};
